import PropTypes from 'prop-types';
import React, { Component, Fragment } from 'react';
import { UX2 } from '@wsb/guac-widget-core';
import DurationAndCost from '../../common/components/DurationAndCost';
import dataAids from '../../common/constants/dataAids';
import FormattersStore from '../../common/stores/Formatters';
import Field from '../../common/constants/editable-field-tags';
import ServiceListItemAsset from '../../common/components/ServiceListItemAsset';
import imageCropOptions from '../../constants/imageCropOptions';
import { isSingleEvent } from '../../common/utils/recurrenceType';
import SelectServiceButton from '../../common/components/SelectServiceButton';
import { Truncate } from '@wsb/ola-widget-components';
import { trackMetric } from '../../common/utils/trackMetric';
import {
  SERVICE_LIST_EXPAND_SERVICE_DESCRIPTION_CTA_CLICK,
  SERVICE_LIST_EXPAND_SERVICE_DESCRIPTION_CTA_IMPRESSION
} from '../../common/constants/metrics';

const styles = {
  serviceListItem: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
    paddingBottom: 'xxlarge',
    '@xs-only': {
      flexDirection: 'column'
    }
  },
  titleAndDetails: {
    flexGrow: '1',
    flex: '1',
    minWidth: '0',
    '@sm': {
      marginTop: '-xsmall'
    }
  },
  descriptionDesktop: {
    marginTop: 'xxsmall',
    marginBottom: '0',
    '@xs-only': {
      display: 'none'
    }
  },
  descriptionMobile: {
    marginTop: 'xxsmall',
    marginBottom: 'small',
    '@sm': {
      display: 'none'
    }
  },
  buttonWrapper: {
    '@xs-only': {
      width: 'auto'
    },
    '@sm': {
      flexShrink: '0',
      marginLeft: 'medium',
      maxWidth: '33.33333333333333%'
    },
    '@md': {
      maxWidth: '25%'
    }
  },
  button: {
    '@xs-only': {
      width: 'auto',
      paddingHorizontal: 'small'
    },
    '@sm': {
      paddingHorizontal: 'small'
    },
    '@md': {
      paddingHorizontal: 'small'
    },
    '@lg': {
      paddingHorizontal: 'small'
    }
  },
  descriptionStyles: {
    container: {
      display: 'block',
      marginTop: 'xxsmall'
    },
    truncated: {
      wordWrap: 'break-word'
    },
    expanded: {
      wordWrap: 'break-word'
    },
    moreLink: {
      display: 'block',
      marginTop: 'xxsmall'
    }
  }
};

export default class ServiceListItem extends Component {
  assetStyles() {
    const { imageCropMethod, widgetCategory } = this.props;
    const isBorderWidthShown =
      imageCropMethod === imageCropOptions.EXPAND_TO_FILL || widgetCategory !== 'neutral';

    return {
      backgroundColor: '!rgba(255,255,255,.2)',
      borderWidth: isBorderWidthShown ? '0' : '1px',
      borderStyle: 'solid',
      borderColor: 'section',
      '@xs-only': {
        width: '100%',
        marginBottom: 'xsmall'
      },
      '@sm': {
        width: '120px',
        flexShrink: '0',
        marginRight: 'medium'
      },
      '@md': {
        width: '145px'
      },
      '@lg': {
        width: '170px'
      }
    };
  }

  render() {
    const {
      service,
      maxServiceDescriptionEllipseCharacters,
      navigate,
      staticContent,
      callToActionText,
      imageCropMethod,
      renderMode
    } = this.props;
    const {
      Element: { Block, Heading, Details, MoreLink, Text }
    } = UX2;
    const { dateTimeFormatter } = FormattersStore.getState();
    const hasAsset = Boolean(service.image_url);
    const mobileMaxEllipseCharacters = maxServiceDescriptionEllipseCharacters / 2;
    const serviceListItemStyles = {
      ...styles.serviceListItem,
      '@sm': {
        alignItems: service.description && service.description.length ? 'flex-start' : 'center'
      }
    };
    const truncateProps = {
      isCollapsible: true,
      style: styles.descriptionStyles,
      expandCtaAs: MoreLink.Expand,
      onClickMore: () => trackMetric(SERVICE_LIST_EXPAND_SERVICE_DESCRIPTION_CTA_CLICK),
      onExpandCtaMount: () => trackMetric(SERVICE_LIST_EXPAND_SERVICE_DESCRIPTION_CTA_IMPRESSION),
      i18n: {
        expandCta: staticContent.more,
        collapseCta: staticContent.less
      }
    };

    return (
      <Block style={ serviceListItemStyles }>
        { hasAsset && (
          <Block style={ this.assetStyles() }>
            <ServiceListItemAsset imageCropMethod={ imageCropMethod } assetUrl={ service.image_url } />
          </Block>
        ) }
        <Block style={ styles.titleAndDetails }>
          <Heading.Product data-aid={ dataAids.SERVICE_LIST_ITEM_SERVICE_NAME }>
            { service.name }
          </Heading.Product>
          <DurationAndCost
            service={ service }
            staticContent={ staticContent }
            as={ Details }
            withPaymentLabels={ false }
          />
          { isSingleEvent(service.recurrence_type) && (
            <Details data-aid={ dataAids.SERVICE_LIST_ITEM_SERVICE_EVENT_DATE }>
              { dateTimeFormatter.formatDateTime(service.start_time) }
            </Details>
          ) }
          <Fragment>
            <Text
              style={ styles.descriptionDesktop }
              data-aid={ dataAids.SERVICE_LIST_ITEM_SERVICE_DESCRIPTION }
            >
              <Truncate
                { ...truncateProps }
                maxEllipseCharacters={ maxServiceDescriptionEllipseCharacters }
              >
                { service.description }
              </Truncate>
            </Text>
            <Text
              style={ styles.descriptionMobile }
              data-aid={ dataAids.SERVICE_LIST_ITEM_SERVICE_DESCRIPTION_MOBILE }
            >
              <Truncate { ...truncateProps } maxEllipseCharacters={ mobileMaxEllipseCharacters }>
                { service.description }
              </Truncate>
            </Text>
          </Fragment>
        </Block>
        <Block style={ styles.buttonWrapper }>
          <SelectServiceButton
            data-aid={ dataAids.SERVICE_LIST_ITEM_BOOK_BUTTON }
            style={ styles.button }
            data-route={ Field.CTA }
            size='small'
            navigate={ navigate }
            service={ service }
            renderMode={ renderMode }
          >
            { callToActionText || staticContent.book }
          </SelectServiceButton>
        </Block>
      </Block>
    );
  }
}

ServiceListItem.propTypes = {
  service: PropTypes.object,
  maxServiceDescriptionEllipseCharacters: PropTypes.number.isRequired,
  staticContent: PropTypes.object.isRequired,
  callToActionText: PropTypes.string.isRequired,
  imageCropMethod: PropTypes.string,
  widgetCategory: PropTypes.string,
  navigate: PropTypes.func.isRequired,
  renderMode: PropTypes.string.isRequired
};
